// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useCallback } from "react";
import {
  AlertCircle,
  RefreshCw,
  Bot,
  CheckCircle,
  Zap,
  DollarSign,
  Download,
} from "lucide-react";
import DataTable from "react-data-table-component";
import llmUsageService from "../../services/llmUsageService";
import useTenants from "../../hooks/useTenants";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess } from "../../config/roleConfig";
import {
  STANDARD_DATE_RANGES,
  getDefaultStandardRange,
} from "../../utils/dateRanges";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import { extractPaginatedResponse } from "../../utils/apiHelper";
import {
  getDateFilter,
  isCustomRangeIncomplete,
} from "../../utils/dateFilterHelpers";
import { useTheme } from "../../context/ThemeContext";

const LLMUsageReports = () => {
  const { userRoles } = useRoutes();
  const { isDarkMode } = useTheme();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  const {
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
    error: tenantsError,
    tenantOptions,
    isAllTenantsSelected,
  } = useTenants(getAdminTenantConfig(isSuperAdmin));

  const [selectedRange, setSelectedRange] = useState(getDefaultStandardRange());
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageSummary, setUsageSummary] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [dailyMetrics, setDailyMetrics] = useState(null);
  const [regelwerkDistribution, setRegelwerkDistribution] = useState(null);
  const [performanceToday, setPerformanceToday] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [totalRuns, setTotalRuns] = useState(0);

  const fetchRecentRuns = useCallback(
    async (page = 1, perPageCount = 20) => {
      // Guard: Don't fetch if Custom Range is selected but dates are incomplete
      if (isCustomRangeIncomplete(selectedRange, customStart, customEnd)) {
        return;
      }

      try {
        const params = {
          limit: perPageCount,
          offset: (page - 1) * perPageCount,
        };

        // Add tenant filtering if not "All Tenants"
        if (!isAllTenantsSelected()) {
          params.tenant_id = selectedTenant;
        }

        // Add date filtering
        const dateFilter = getDateFilter(selectedRange, customStart, customEnd);
        if (dateFilter) {
          params.date_filter = dateFilter.date_filter;
          if (dateFilter.start_date) {
            params.start_date = dateFilter.start_date;
          }
          if (dateFilter.end_date) {
            params.end_date = dateFilter.end_date;
          }
        }

        const response = await llmUsageService.getLLMRuns(params);
        const { data, total } = extractPaginatedResponse(response);

        setRecentRuns(transformRecentRuns(data));
        setTotalRuns(total);
      } catch (err) {
        console.error("Error fetching recent runs:", err);
        setRecentRuns([]);
        setTotalRuns(0);
      }
    },
    [
      selectedTenant,
      isAllTenantsSelected,
      selectedRange,
      customStart,
      customEnd,
    ],
  );

  const fetchData = useCallback(async () => {
    // Guard: Don't fetch if Custom Range is selected but dates are incomplete
    if (isCustomRangeIncomplete(selectedRange, customStart, customEnd)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      // Get date filter parameters
      const dateFilter = getDateFilter(selectedRange, customStart, customEnd);
      const dateFilterValue = dateFilter?.date_filter || null;
      const startDate = dateFilter?.start_date || null;
      const endDate = dateFilter?.end_date || null;

      // Fallback to days if date_filter is not available
      const days =
        selectedRange.label === "Custom Range" && customStart && customEnd
          ? null
          : selectedRange.days;

      // Pass tenant ID to all API calls (null if "All Tenants" is selected)
      const tenantId = isAllTenantsSelected() ? null : selectedTenant;

      const summaryData = await llmUsageService.getUsageSummary(
        days,
        tenantId,
        dateFilterValue,
        startDate,
        endDate,
      );
      const [performanceData, dailyData, distributionData, todayData] =
        await Promise.all([
          llmUsageService.getPerformanceMetrics(
            days,
            tenantId,
            dateFilterValue,
            startDate,
            endDate,
          ),
          llmUsageService.getDailyMetrics(
            days,
            tenantId,
            dateFilterValue,
            startDate,
            endDate,
          ),
          llmUsageService.getRegelwerkDistribution(
            days,
            tenantId,
            dateFilterValue,
            startDate,
            endDate,
          ),
          llmUsageService.getPerformanceToday(
            days,
            tenantId,
            dateFilterValue,
            startDate,
            endDate,
          ),
        ]);
      setUsageSummary(summaryData);
      setPerformanceMetrics(performanceData);
      setDailyMetrics(dailyData);
      setRegelwerkDistribution(distributionData);
      setPerformanceToday(todayData);
      await fetchRecentRuns(1, 20);
    } catch (err) {
      console.error("Error fetching LLM usage data:", err);
      setError("Failed to load data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedRange,
    selectedTenant,
    isAllTenantsSelected,
    fetchRecentRuns,
    customStart,
    customEnd,
  ]);

  useEffect(() => {
    fetchData();
  }, [selectedTenant, selectedRange, customStart, customEnd, fetchData]);

  const llmMetrics = [
    {
      label: "Total LLM Runs Today",
      icon: Bot,
      value: (
        performanceToday?.total_requests?.today ??
        dailyMetrics?.total_requests ??
        0
      ).toLocaleString(),
      change:
        performanceToday?.total_requests?.change_text || "+15.3% vs yesterday",
      trend: "up",
      color: "purple",
    },
    {
      label: "Success Rate",
      icon: CheckCircle,
      value: `${Number(performanceToday?.success_rate?.today ?? usageSummary?.success_rate ?? 0).toFixed(1)}%`,
      change: performanceToday?.success_rate?.change_text || "+0.5% better",
      trend: "up",
      color: "green",
    },
    {
      label: "Avg Response Time",
      icon: Zap,
      value: `${Math.round(performanceToday?.avg_response_time_ms?.today ?? performanceMetrics?.average_response_time ?? 0)}ms`,
      change:
        performanceToday?.avg_response_time_ms?.change_text || "-120ms faster",
      trend:
        (performanceToday?.avg_response_time_ms?.change_pct ?? -1) > 0
          ? "up"
          : "down",
      color: "orange",
    },
    {
      label: "Total Cost Today",
      icon: DollarSign,
      value: `${Number(performanceToday?.energy_consumption_kwh?.today ?? 0).toFixed(2)}`,
      change: performanceToday?.energy_consumption_kwh?.change_text || "0",
      trend: "down",
      color: "emerald",
    },
  ];
  const llmStats = [
    {
      label: "Total Tokens",
      value:
        performanceMetrics?.total_tokens_processed_pretty ||
        `${((performanceMetrics?.total_tokens_in || 0) + (performanceMetrics?.total_tokens_out || 0)).toFixed(1)}M`,
      detail: `In: ${performanceMetrics?.total_tokens_in_pretty || 0} • Out: ${performanceMetrics?.total_tokens_out_pretty || 0}`,
    },
    {
      label: "Avg Speed",
      value: Number(
        performanceMetrics?.average_speed_tokens_per_second || 0,
      ).toFixed(1),
      detail: "tokens/sec",
    },
    {
      label: "Total Duration",
      value: `${performanceMetrics?.average_response_time?.toFixed(2) || 0}h`,
      detail: "Processing time",
    },
    {
      label: "Failed Runs",
      value: `${performanceMetrics?.total_failed_runs || 0}`,
      detail: usageSummary?.error_rate
        ? `${usageSummary.error_rate}% error rate`
        : "",
    },
  ];

  const computeScores = () => {
    if (!regelwerkDistribution) return [];
    const { allgemein, ruleset1, ruleset2 } =
      regelwerkDistribution?.averages || {};
    return [
      {
        title: "Allgemeine Bewertung",
        score: allgemein,
        subtitle: "General Assessment Score",
      },
      {
        title: "Regelwerk 1",
        score: ruleset1,
        subtitle: "Rule Set 1 Compliance",
      },
      {
        title: "Regelwerk 2",
        score: ruleset2,
        subtitle: "Rule Set 2 Compliance",
      },
    ];
  };

  const classificationFromDistribution = (() => {
    if (!regelwerkDistribution?.label) return [];
    const counts = regelwerkDistribution.label.counts || {};
    const percentages = regelwerkDistribution.label.percentages || {};
    const labelMap = { V: "V", U: "G", E: "R" };
    const meta = {
      V: { name: "Veranstaltung", color: "emerald" },
      G: { name: "Gemeinnützig", color: "orange" },
      R: { name: "Risiko", color: "red" },
    };
    return ["V", "U", "E"].map((src) => {
      const mapped = labelMap[src];
      const info = meta[mapped];
      return {
        badge: mapped,
        name: info.name,
        count: `${(counts[src] || 0).toLocaleString()} bookings`,
        percent: `${Number(percentages[src] || 0).toFixed(2)}%`,
        color: info.color,
      };
    });
  })();

  const transformRecentRuns = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((run, index) => {
      const formatDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
      };

      const mapping = { E: "R", U: "G", V: "V" };

      // Create unique ID by combining booking_id, run_type, and index
      const uniqueId = `${run.booking_id?.substring(0, 8) || "Unknown"}-${run.run_type || "initial"}-${index}`;

      return {
        id: uniqueId,
        bookingId: run.booking_number || "Unknown",
        fullBookingId: run.booking_number,
        type: run.run_type || "initial",
        status:
          run.status === "completed" &&
          (!run.error_message || run.error_message === "")
            ? "success"
            : "warning",
        duration: formatDuration(run.duration_ms || 0),
        tokens: `${((run.input_tokens || 0) + (run.output_tokens || 0)).toLocaleString()} (${(run.input_tokens || 0).toLocaleString()}/${(run.output_tokens || 0).toLocaleString()})`,
        cost: `${Number(run.total_cost || 0).toFixed(3)} KWh`,
        scores: `${run.llm_allgemein || 0} / ${run.llm_regelwerk1 || 0} / ${run.llm_regelwerk2 || 0}`,
        class: mapping[run.llm_kennzeichnung] || run.llm_kennzeichnung || "--",
        errorMessage: run.error_message,
      };
    });
  };

  const getClassificationColor = (classification) => {
    const colors = {
      V: "bg-gradient-to-br bg-primary-color dark:bg-primary-color/40",
      G: "bg-gradient-to-br bg-primary-color dark:bg-primary-color/40",
      R: "bg-gradient-to-br bg-primary-color dark:bg-primary-color/40",
    };
    return colors[classification] || "bg-gray-400";
  };

  // DataTable columns
  const columns = [
    {
      name: "Booking ID",
      selector: (row) => row.id,
      cell: (row) => (
        <code className="text-sm font-mono bg-gray-100 dark:bg-slate-700 dark:text-slate-200 px-2 py-1 rounded">
          {row.bookingId}
        </code>
      ),
      sortable: true,
      width: "170px",
    },
    {
      name: "Run Type",
      selector: (row) => row.type,
      sortable: true,
      width: "120px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) =>
        row.status === "success" ? (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full text-xs font-medium">
            ✓ Completed
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs font-medium">
            ⚠ {row.errorMessage ? "Error" : "Warning"}
          </span>
        ),
      sortable: true,
      width: "160px",
    },
    {
      name: "Duration",
      selector: (row) => row.duration,
      sortable: true,
      width: "140px",
    },
    {
      name: "Tokens",
      selector: (row) => row.tokens,
      sortable: false,
      width: "220px",
    },
    {
      name: "Cost",
      selector: (row) => row.cost,
      sortable: true,
      width: "120px",
    },
    {
      name: "Scores",
      selector: (row) => row.scores,
      sortable: false,
      width: "160px",
    },
    {
      name: "Classification",
      selector: (row) => row.class,
      cell: (row) =>
        row.class !== "--" ? (
          <div className="flex justify-center">
            <div
              className={`w-7 h-7 rounded ${getClassificationColor(row.class)} flex items-center justify-center text-white text-sm font-bold`}
            >
              {row.class}
            </div>
          </div>
        ) : (
          <div className="flex justify-center">--</div>
        ),
      sortable: true,
      width: "150px",
    },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: isDarkMode ? "#1e293b" : "white",
        width: "100%",
        tableLayout: "fixed",
      },
    },
    headRow: {
      style: {
        backgroundColor: isDarkMode ? "#334155" : "#f9fafb",
        borderBottom: isDarkMode ? "2px solid #475569" : "2px solid #e5e7eb",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: "600",
        color: isDarkMode ? "#94a3b8" : "#64748b",
        textTransform: "uppercase",
        paddingLeft: "16px",
        paddingRight: "16px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
    rows: {
      style: {
        minHeight: "64px",
        borderBottom: isDarkMode ? "1px solid #334155" : "1px solid #f3f4f6",
        backgroundColor: isDarkMode ? "#1e293b" : "transparent",
        "&:hover": {
          backgroundColor: isDarkMode ? "#334155" : "#f9fafb",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "16px",
        paddingRight: "16px",
        fontSize: "14px",
        color: isDarkMode ? "#e2e8f0" : "#334155",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  };

  const handlePageChange = (page) => {
    fetchRecentRuns(page, 20);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    fetchRecentRuns(page, newPerPage);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="LLM Usage Reports"
        subtitle={`Real-time monitoring • Auto-refresh: 30s • Last updated: ${new Date().toLocaleTimeString("de-DE")}`}
        status="All Systems Operational"
        statusColor="green"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
        ranges={STANDARD_DATE_RANGES.map((r) => r.label)}
        selectedRange={selectedRange.label}
        onRangeChange={(value) => {
          const range = STANDARD_DATE_RANGES.find((r) => r.label === value);
          setSelectedRange(range);
          if (range.label !== "Custom Range") {
            setCustomStart("");
            setCustomEnd("");
          }
        }}
        buttons={[
          {
            text: "Export Live Report",
            icon: <Download className="h-4 w-4" />,
            // TODO: wire up export — no backend endpoint yet
            onClick: () => {},
            className:
              "bg-primary-color text-white px-4 py-2 rounded-md hover:bg-primary-color-dark flex items-center gap-2",
          },
        ]}
      />

      {/* Custom Date Range Inputs */}
      {selectedRange.label === "Custom Range" && (
        <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 lg:px-6 py-3">
          <div className="flex items-center justify-end space-x-2">
            <span className="text-sm text-gray-600 dark:text-slate-400">
              Custom Date Range:
            </span>
            <input
              type="date"
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span className="text-gray-500 dark:text-slate-500">to</span>
            <input
              type="date"
              className="border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
          </div>
        </div>
      )}

      {isLoading && <PageLoader message="Lade LLM-Nutzungsdaten..." />}

      {/* Main Content */}
      {!isLoading && (
        <div className="mx-auto px-4 lg:px-4 py-4">
          {/* Error Messages */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <span className="text-red-800 dark:text-red-200">{error}</span>
              </div>
            </div>
          )}

          {tenantsError && (
            <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                <span className="text-yellow-800 dark:text-yellow-200">
                  {tenantsError} - Using fallback tenant data
                </span>
              </div>
            </div>
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {llmMetrics.map((metric, idx) => {
              const IconComponent = metric.icon;
              return (
                <div
                  key={idx}
                  className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      {metric.label}
                    </div>
                    <div
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br bg-primary-color dark:bg-primary-color/40 flex items-center justify-center text-white`}
                    >
                      <IconComponent className="w-5 h-5" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                    {metric.value}
                  </div>
                  <div className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                    <span>{metric.trend === "up" ? "↗" : "↘"}</span>
                    <span>{metric.change}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Token Usage Statistics */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm mb-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Token Usage & Performance
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {llmStats.map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4 border border-gray-200 dark:border-slate-600"
                >
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                    {stat.label}
                  </div>
                  <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    {stat.value}
                  </div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {stat.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* LLM Score Distributions */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm mb-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Average LLM Scores
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {computeScores().map((score, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-slate-700/30 border-2 border-gray-200 dark:border-slate-600 rounded-lg p-5 text-center"
                >
                  <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-3">
                    {score.title}
                  </div>
                  <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gradient-to-br bg-primary-color dark:bg-primary-color/40 flex flex-col items-center justify-center text-white">
                    <div className="text-3xl font-bold">
                      {score.score ?? "--"}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {score.subtitle}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Classification Breakdown */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm mb-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Event Classification Distribution
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {classificationFromDistribution.map((cls, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-slate-700/30 border-2 border-gray-200 dark:border-slate-600 rounded-lg p-5 text-center hover:border-blue-500 dark:hover:border-blue-400 hover:-translate-y-1 transition-all"
                >
                  <div
                    className={`w-16 h-16 mx-auto mb-3 rounded-full ${getClassificationColor(cls.badge)} flex items-center justify-center text-white text-3xl font-bold`}
                  >
                    {cls.badge}
                  </div>
                  <div className="font-semibold text-slate-800 dark:text-slate-100 mb-1">
                    {cls.name}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    {cls.count}
                  </div>
                  <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    {cls.percent}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent LLM Runs with DataTable */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 shadow-sm">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                Recent LLM Executions
              </h2>
            </div>
            <div className="w-full overflow-x-auto">
              <DataTable
                columns={columns}
                data={recentRuns}
                keyField="id"
                customStyles={customStyles}
                selectableRows={false}
                pagination
                paginationServer
                paginationPerPage={20}
                paginationRowsPerPageOptions={[20, 30, 50, 75, 100]}
                paginationTotalRows={totalRuns}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                noDataComponent="Keine Nutzer gefunden"
                progressPending={isLoading}
                progressComponent={
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                  </div>
                }
                highlightOnHover
                pointerOnHover
                fixedHeader
              />
            </div>
          </div>

          {/* Footer Status Bar */}
          <div className="mt-8 bg-gray-100 dark:bg-slate-800 rounded-lg dark:border dark:border-slate-700 p-4 flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <div
                  className={`w-2 h-2 rounded-full mr-2 ${isLoading ? "bg-yellow-500 dark:bg-yellow-400 animate-pulse" : "bg-green-500 dark:bg-green-400"}`}
                ></div>
                <span className="text-sm text-gray-600 dark:text-slate-400">
                  {isLoading ? "Loading..." : "Live Data"}
                </span>
              </div>
              <span className="text-sm text-gray-500 dark:text-slate-500">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Export Report
              </button>
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Configure Alerts
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMUsageReports;
